import { safeNumber } from '../../../services/function';

const round2 = (value: number) => parseFloat(value.toFixed(2));

const pct = (base: number, rate: number) => base * (-rate / 100);

/**
 * Recalcule le totalNet exactement comme Simulator.tsx (forward pass synchrone).
 * Utilisé uniquement pour le gross-up net → brut.
 */
export function computeTotalNetFromBrutGain(
  state: Record<string, any>,
  allData: Record<string, any>,
  salaireGainBrut: number
): number {
  const salaireBaseHeures = safeNumber(state.salaireBaseHeures);
  const heuresSup125 = safeNumber(state.heuresSup125);
  const heuresSup150 = safeNumber(state.heuresSup150);
  const primeAnciennetePourcentage = safeNumber(state.primeAnciennetePourcentage);
  const bonusPrime = safeNumber(state.bonusPrime);
  const avantageEnNature = safeNumber(state.avantageEnNature);

  if (!salaireBaseHeures || !salaireGainBrut) return 0;

  const salaireTaux = round2(salaireGainBrut / salaireBaseHeures);
  const tauxHeuresSup125 = round2(salaireTaux * 1.25);
  const salaireGainHeuresSup125 = round2(heuresSup125 * tauxHeuresSup125);
  const tauxHeuresSup150 = round2(salaireTaux * 1.5);
  const salaireGainHeuresSup150 = round2(heuresSup150 * tauxHeuresSup150);
  const primeAncienneteMontant = round2(salaireGainBrut * primeAnciennetePourcentage / 100);

  const totalGain = round2(
    salaireGainBrut +
    salaireGainHeuresSup125 +
    salaireGainHeuresSup150 +
    primeAncienneteMontant +
    bonusPrime +
    avantageEnNature
  );

  const totalHeures = salaireBaseHeures + heuresSup125 + heuresSup150;

  const car = Math.min(totalGain, safeNumber(allData?.car_amount));
  const carPartSalariale = pct(car, safeNumber(allData?.car_partSalariale));

  const poleEmploiBase = safeNumber(allData?.france_travail_amount) *
    (salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_mon_gasque_hours));
  const poleEmploi = Math.min(totalGain, poleEmploiBase);
  const poleEmploiPartSalariale = allData?.AffilAssChomage
    ? pct(poleEmploi, safeNumber(allData?.france_travail_partSalariale))
    : 0;

  let cmrcTaux = 0;
  if (totalHeures >= safeNumber(allData?.horaire_temps_plein_cmrc_hours)) {
    cmrcTaux = Math.min(totalGain, safeNumber(allData?.cmrc_ta_amount));
  } else {
    const taBase = safeNumber(allData?.cmrc_ta_amount) *
      (salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_cmrc_hours));
    cmrcTaux = Math.min(totalGain, taBase);
  }
  const cmrcTauxPartSalariale = pct(cmrcTaux, safeNumber(allData?.cmrc_ta_partSalariale));

  let cmrcTB = 0;
  if (totalHeures >= safeNumber(allData?.horaire_temps_plein_cmrc_hours)) {
    if (totalGain > safeNumber(allData?.cmrc_ta_amount) * 8) {
      cmrcTB = safeNumber(allData?.cmrc_tb_amount);
    } else {
      cmrcTB = Math.max(0, totalGain - safeNumber(allData?.cmrc_ta_amount));
    }
  } else {
    const total8 = safeNumber(allData?.cmrc_ta_amount) * 8 *
      (salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_cmrc_hours));
    if (totalGain > total8) {
      cmrcTB = safeNumber(allData?.cmrc_tb_amount) *
        (salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_cmrc_hours));
    } else {
      cmrcTB = Math.max(0, totalGain - safeNumber(allData?.cmrc_ta_amount) *
        (salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_cmrc_hours)));
    }
  }
  const cmrcTBPartSalariale = pct(cmrcTB, safeNumber(allData?.cmrc_tb_partSalariale));

  const ccpbPrevoyance = totalGain;
  const ccpbPrevoyancePartSalariale =
    allData?.AffilCCPB_Ouvriers && safeNumber(allData?.ccpb_pr_voyance_partSalariale) !== 0
      ? pct(ccpbPrevoyance, safeNumber(allData?.ccpb_pr_voyance_partSalariale))
      : 0;

  let prevoyanceT1CCSS = 0;
  if (allData?.PrevoyanceT1_CCSS) {
    const t1Base = safeNumber(allData?.ccss__cgcs__amount) *
      (salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_mon_gasque_hours));
    prevoyanceT1CCSS = Math.min(totalGain, t1Base);
  } else {
    prevoyanceT1CCSS = Math.min(totalGain, safeNumber(allData?.ccss__cgcs__amount));
  }
  const prevoyanceT1CCSSPartSalariale =
    safeNumber(allData?.prevoyanceT1_PartSalariale) !== 0
      ? pct(prevoyanceT1CCSS, safeNumber(allData?.prevoyanceT1_PartSalariale))
      : 0;

  const prevoyanceTASSFRBase = safeNumber(allData?.plafond_ss_francais_amount) *
    (salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_mon_gasque_hours));
  const prevoyanceTASSFR = Math.min(totalGain, prevoyanceTASSFRBase);
  const prevoyanceTASSFRPartSalariale =
    safeNumber(allData?.prevoyanceTA_PartSalariale) !== 0
      ? pct(prevoyanceTASSFR, safeNumber(allData?.prevoyanceTA_PartSalariale))
      : 0;

  const plafondRatio = salaireBaseHeures / safeNumber(allData?.horaire_temps_plein_mon_gasque_hours);
  const plafond4 = safeNumber(allData?.plafond_ss_francais_amount) * 4 * plafondRatio;

  let prevoyanceT2CCSS = 0;
  if (allData?.PrevoyanceT1_CCSS) {
    if (totalGain > plafond4) {
      prevoyanceT2CCSS = safeNumber(allData?.prevoyanceT2_Amount) * plafondRatio;
    } else {
      prevoyanceT2CCSS = Math.max(0, totalGain - safeNumber(allData?.prevoyanceT1_Amount) * plafondRatio);
    }
  } else if (totalGain > plafond4) {
    prevoyanceT2CCSS = safeNumber(allData?.prevoyanceT2_Amount);
  } else {
    prevoyanceT2CCSS = Math.max(0, totalGain - safeNumber(allData?.prevoyanceT1_Amount));
  }
  const prevoyanceT2CCSSPartSalariale =
    safeNumber(allData?.prevoyanceT2_PartSalariale) !== 0
      ? pct(prevoyanceT2CCSS, safeNumber(allData?.prevoyanceT2_PartSalariale))
      : 0;

  let prevoyanceTB = 0;
  if (totalGain > plafond4) {
    prevoyanceTB = safeNumber(allData?.prevoyanceTB_Amount) * plafondRatio;
  } else {
    prevoyanceTB = Math.max(0, totalGain - safeNumber(allData?.plafond_ss_francais_amount) * plafondRatio);
  }
  const prevoyanceTBPartSalariale =
    safeNumber(allData?.prevoyanceTB_PartSalariale) !== 0
      ? pct(prevoyanceTB, safeNumber(allData?.prevoyanceTB_PartSalariale))
      : 0;

  const plafond8 = safeNumber(allData?.plafond_ss_francais_amount) * 8 * plafondRatio;
  let prevoyanceTC = 0;
  if (totalGain > plafond8) {
    prevoyanceTC = safeNumber(allData?.prevoyanceTC_Amount) * plafondRatio;
  } else if (totalGain > plafond4) {
    prevoyanceTC = totalGain - safeNumber(allData?.prevoyanceTA_Amount) * 4 * plafondRatio;
  }

  const prevoyanceTCPartSalariale =
    safeNumber(allData?.prevoyanceTC_PartSalariale) !== 0
      ? pct(prevoyanceTC, safeNumber(allData?.prevoyanceTC_PartSalariale))
      : 0;

  const cotisationAT = totalGain;
  const cotisationATPartSalariale =
    safeNumber(allData?.cotisationAT_PartSalariale) !== 0
      ? pct(cotisationAT, safeNumber(allData?.cotisationAT_PartSalariale))
      : 0;

  const totalPartSalariale =
    carPartSalariale +
    poleEmploiPartSalariale +
    cmrcTauxPartSalariale +
    cmrcTBPartSalariale +
    safeNumber(state.ccpbCongesFormationsPartSalariale) +
    ccpbPrevoyancePartSalariale +
    prevoyanceT1CCSSPartSalariale +
    prevoyanceTASSFRPartSalariale +
    prevoyanceT2CCSSPartSalariale +
    prevoyanceTBPartSalariale +
    prevoyanceTCPartSalariale +
    safeNumber(state.fraisDeSantePartSalariale) +
    cotisationATPartSalariale;

  // 5% monégasque — reproduit exactement Simulator.tsx (allData.salaireTaux, pas le taux calculé)
  let montant5Monegasque = 0;
  if (salaireTaux !== 0) {
    const calcul5 = (salaireGainBrut + salaireGainHeuresSup125 + salaireGainHeuresSup150 + primeAncienneteMontant) * 0.05;
    if (allData?.indemnite5) {
      montant5Monegasque = calcul5;
    } else if (allData?.salaireTaux <= allData?.smic_amount + 0.01) {
      montant5Monegasque = calcul5;
    }
  }

  const montantSalarialAutoTitresRestaurant =
    safeNumber(state.nbTitresRestaurant) !== 0 && safeNumber(state.montantSalarialTitresRestaurant) !== 0
      ? safeNumber(state.nbTitresRestaurant) * (-safeNumber(state.montantSalarialTitresRestaurant))
      : 0;

  const montantSalarialAutoPaniers =
    safeNumber(state.nbPaniers) !== 0 && safeNumber(state.montantSalarialPaniers) !== 0
      ? safeNumber(state.nbPaniers) * safeNumber(state.montantSalarialPaniers)
      : 0;

  const avantageEnNaturePatronale = avantageEnNature !== 0 ? -avantageEnNature : 0;

  return round2(
    totalGain +
    totalPartSalariale +
    montantSalarialAutoTitresRestaurant +
    montantSalarialAutoPaniers +
    montant5Monegasque +
    avantageEnNaturePatronale +
    safeNumber(state.remboursementTransportPatronale) +
    safeNumber(state.indemniteTeletravail) +
    safeNumber(state.fraisDiversesPatronale)
  );
}

/** Recherche dichotomique + affinage au centime pour limiter l'écart sur totalNet. */
export function grossUpSalaireGainFromNet(
  targetNet: number,
  state: Record<string, any>,
  allData: Record<string, any>
): number {
  if (!targetNet || targetNet <= 0 || !state.salaireBaseHeures) return 0;

  let low = targetNet;
  let high = targetNet * 2.5;
  let bestBrut = round2((low + high) / 2);
  let bestError = Infinity;

  for (let i = 0; i < 60; i++) {
    const mid = round2((low + high) / 2);
    const computedNet = computeTotalNetFromBrutGain(state, allData, mid);
    const error = Math.abs(computedNet - targetNet);

    if (error < bestError) {
      bestError = error;
      bestBrut = mid;
    }

    if (computedNet < targetNet) {
      low = mid;
    } else {
      high = mid;
    }

    if (high - low < 0.005) break;
  }

  const baseCents = Math.round(bestBrut * 100);
  for (let cents = baseCents - 15; cents <= baseCents + 15; cents++) {
    const candidate = round2(cents / 100);
    const error = Math.abs(computeTotalNetFromBrutGain(state, allData, candidate) - targetNet);
    if (error < bestError) {
      bestError = error;
      bestBrut = candidate;
    }
  }

  return bestBrut;
}
